import type { Permission } from '@ctcells/core'
import { t, type MessageKey } from '@ctcells/i18n'

/**
 * 화면 공통 접근 상태 — 버튼 활성을 가르는 세 축(락 · 상태 · 권한)의 입력.
 *
 * 🔴 **프론트가 상태를 추론하지 않는다.** 여기 값은 전부 서버·구독이 준 것을 받아 나르는 것이고,
 *    화면이 계산해 만들지 않는다. 특히 `allowed_commands` 는 그대로 쓴다(§11-3 #3).
 */
export interface ScreenAccess {
  /**
   * 서버가 준 보유 권한. `undefined` = 아직 모름 → 조작 버튼을 **비활성 + 사유**로 둔다.
   * 모르는 상태에서 눌리게 두지 않는다(안전한 쪽으로 기운 기본값).
   */
  permissions?: readonly Permission[] | undefined
  /** 제어락을 내가 쥐고 있는가 */
  lockHeld?: boolean | undefined
  /**
   * 구독이 끊겼거나 stale. `true` 면 상태변경 버튼을 전부 비활성하고
   * 「연결 끊김 — 제어 불가」를 화면에 **표시**한다(CLAUDE.md 규약 · §11-3 #1).
   */
  controlDisabled?: boolean | undefined
  /**
   * 붙었던 구독이 **끊겼다** — 「연결 끊김 — 제어 불가」 띠(`OfflineNotice`)는 이것만 본다.
   * 🔴 `controlDisabled` 와 갈라 둔 까닭: 새로고침 직후 붙는 중에도 제어는 막지만(모르면 막는다) 띠는 띄우지
   *    않는다 — 화면을 열 때마다 붉은 줄이 번쩍이던 것(eric 2026-09-22)이 그것이었다.
   */
  offline?: boolean | undefined
}

/** 비활성 사유가 붙은 버튼 props. 원자 `DisabledState` 에 그대로 스프레드한다 */
export type ActionGate =
  { disabled: false; disabledReason?: undefined } | { disabled: true; disabledReason: string }

export interface GateOptions {
  /** 이 동작에 필요한 권한 */
  perm: Permission
  /** 제어락이 필요한 동작인가. 기본 false — 조회성 동작은 락과 무관하다 */
  needsLock?: boolean
  /**
   * 지금 상태에서 할 수 있는가. 🔴 이 값은 **서버가 준 집합으로 판정한 결과**를 넘긴다.
   * `status` 를 보고 화면이 만들어 내지 마라.
   */
  stateOk?: boolean
  /** 상태가 막았을 때 쓸 사유 키 */
  stateReason?: MessageKey
}

/**
 * 비활성 사유는 **하나만** 고른다. 우선순위가 곧 "가장 먼저 풀어야 할 조건"이다(README §5-8).
 *
 * ```
 * 연결 끊김(stale) → 권한 없음 → 제어락 필요 → 지금 상태에서는 할 수 없음
 * ```
 *
 * 🔴 권한이 없어도 **버튼을 숨기지 않는다.** 비활성 + 사유이고 집행은 서버 403 이다(§5-6).
 */
export function gate(access: ScreenAccess, options: GateOptions): ActionGate {
  const { permissions, lockHeld = false, controlDisabled = false } = access
  const { perm, needsLock = false, stateOk = true, stateReason } = options

  if (controlDisabled) return { disabled: true, disabledReason: t('reason.streamStale') }
  if (!(permissions?.includes(perm) ?? false))
    return { disabled: true, disabledReason: t('reason.noPermission', { perm }) }
  if (needsLock && !lockHeld) return { disabled: true, disabledReason: t('reason.lockRequired') }
  if (!stateOk)
    return {
      disabled: true,
      disabledReason: t(stateReason ?? 'reason.commandNotAllowed'),
    }
  return { disabled: false }
}

/** 읽기 전용 동작(내보내기 · 보고서)에는 제어락·스트림 게이트를 걸지 않는다 */
export function viewGate(access: ScreenAccess, perm: Permission): ActionGate {
  return (access.permissions?.includes(perm) ?? false)
    ? { disabled: false }
    : { disabled: true, disabledReason: t('reason.noPermission', { perm }) }
}

/** `view` 권한 보유 여부 — 환자 식별자 마스킹 판단에 쓴다(`maskPii`) */
export function canView(access: ScreenAccess): boolean {
  return access.permissions?.includes('view') ?? false
}

/**
 * 🔴 **`allowed_commands` 에 대한 유일한 연산은 `includes` 다. 여기 말고 어디서도 하지 않는다.**
 *
 * 서버가 상태머신·제어락·RBAC·Pre-flight 를 전부 반영해 준 집합이므로 화면이 `status` 로
 * 추론하거나 부분 병합하지 않는다. 이 함수가 그 연산을 한 곳에 가둔다 —
 * 호출부는 「이 명령이 집합에 있나」만 물을 수 있고, 다른 계산을 넣을 자리가 없다.
 *
 * `allowed === undefined` → **비활성**. 모르면 못 누르는 쪽이 안전한 기본값이다
 * (스냅샷이 아직 안 왔거나 구독이 끊긴 상태가 여기로 온다).
 */
export function commandGate<C extends string>(
  access: ScreenAccess,
  /** 서버가 준 집합 **그대로**. 화면이 만들어 낸 배열을 넣지 마라 */
  allowed: readonly C[] | undefined,
  command: C,
  perm: Permission,
  options?: { needsLock?: boolean; stateReason?: MessageKey },
): ActionGate {
  return gate(access, {
    perm,
    needsLock: options?.needsLock ?? true,
    stateOk: allowed?.includes(command) ?? false,
    ...(options?.stateReason === undefined ? {} : { stateReason: options.stateReason }),
  })
}
